import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';

import { UserRole } from '../../core/auth/auth-session.model';
import { TranslationService } from '../../core/i18n/translation.service';

export type MenuItemId =
  | 'dashboard'
  | 'audits'
  | 'checklists'
  | 'users'
  | 'tenants'
  | 'corrective-actions'
  | 'reports';

export type SidebarActionId = 'report-incident';

interface SidebarItem<TId extends string> {
  id: TId;
  labelKey: string;
  iconClass: string;
  roles: UserRole[];
}

const MAIN_MENU_ITEMS: SidebarItem<MenuItemId>[] = [
  {
    id: 'dashboard',
    labelKey: 'sidebar.menu.dashboard',
    iconClass: 'fa-solid fa-table-cells-large',
    roles: ['AUDITOR', 'MANAGER', 'BUSINESS_ADMIN'],
  },
  {
    id: 'audits',
    labelKey: 'sidebar.menu.audits',
    iconClass: 'fa-solid fa-clipboard',
    roles: ['AUDITOR', 'MANAGER', 'BUSINESS_ADMIN'],
  },
  {
    id: 'checklists',
    labelKey: 'sidebar.menu.checklists',
    iconClass: 'fa-solid fa-square-check',
    roles: ['AUDITOR', 'MANAGER', 'BUSINESS_ADMIN'],
  },
  {
    id: 'users',
    labelKey: 'sidebar.menu.users',
    iconClass: 'fa-solid fa-user-group',
    roles: ['BUSINESS_ADMIN'],
  },
  {
    id: 'tenants',
    labelKey: 'sidebar.menu.tenants',
    iconClass: 'fa-solid fa-building-user',
    roles: ['SUPER_ADMIN'],
  },
  {
    id: 'corrective-actions',
    labelKey: 'sidebar.menu.correctiveActions',
    iconClass: 'fa-solid fa-circle-exclamation',
    roles: ['AUDITOR', 'MANAGER', 'BUSINESS_ADMIN'],
  },
  {
    id: 'reports',
    labelKey: 'sidebar.menu.reports',
    iconClass: 'fa-solid fa-file-lines',
    roles: ['BUSINESS_ADMIN'],
  },
];

const ACTION_MENU_ITEMS: SidebarItem<SidebarActionId>[] = [
  {
    id: 'report-incident',
    labelKey: 'sidebar.menu.reportIncident',
    iconClass: 'fa-solid fa-triangle-exclamation',
    roles: ['AUDITOR', 'MANAGER', 'BUSINESS_ADMIN'],
  },
];


@Component({
  selector: 'app-sidebar-menu',
  imports: [CommonModule],
  templateUrl: './sidebar-menu.component.html',
  styleUrl: './sidebar-menu.component.css',
})
export class SidebarMenuComponent {
  protected readonly i18n = inject(TranslationService);

  readonly isOpen = input(false);
  readonly role = input<UserRole>('BUSINESS_ADMIN');
  readonly appName = input('Juno Audit');
  readonly activeItemId = input<MenuItemId>('dashboard');

  readonly closeRequested = output<void>();
  readonly itemSelected = output<MenuItemId>();
  readonly actionSelected = output<SidebarActionId>();

  protected readonly visibleMainItems = computed(() =>
    MAIN_MENU_ITEMS.filter((item) => item.roles.includes(this.role())),
  );
  protected readonly visibleActionItems = computed(() =>
    ACTION_MENU_ITEMS.filter((item) => item.roles.includes(this.role())),
  );

  protected onClose(): void {
    this.closeRequested.emit();
  }

  protected onItemSelected(itemId: MenuItemId): void {
    this.itemSelected.emit(itemId);
  }

  protected onActionSelected(actionId: SidebarActionId): void {
    this.actionSelected.emit(actionId);
  }
}
